import "./lib/sentry";
import { Sentry, isSentryEnabled } from "./lib/sentry";
import {
  didRecoverFromChunkError,
  installChunkReloadHandler,
} from "./lib/chunk-reload";
import { installTranslateDomGuard } from "./lib/translate-dom-guard";
import { StrictMode, useMemo, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { registerQueryClient } from "./lib/queryClient";
import { PrivyProvider } from "@privy-io/react-auth";
import { useTranslation } from "react-i18next";
import { privyAppId, getPrivyConfig, useMockAuth } from "./config/privy";
import { useUIStore } from "./stores/useUIStore";
import { ThemeProvider } from "./providers/ThemeProvider";
import { LanguageProvider } from "./providers/LanguageProvider";
import { ServiceProvider } from "./services/ServiceProvider";
import { GlobalTransactionModal } from "./components/GlobalTransactionModal";
import { CelebrationLayer } from "./features/celebrations/CelebrationLayer";
import { StaleVersionBanner } from "./components/StaleVersionBanner";
import { DebugPanel } from "./components/DebugPanel";
import { AttentionBubble } from "./features/attention/AttentionBubble";
import {
  createMockRegistry,
  createSupabaseRegistry,
} from "./services/createRegistry";
import { usePolygonClient } from "./hooks/usePolygonClient";
import App from "./App";
import { captureVolunteerCampaign } from "./lib/volunteerCampaign";
import { captureCreatorCampaign } from "./lib/creatorCampaign";
import "./lib/i18n";
import "./index.css";

if (didRecoverFromChunkError() && isSentryEnabled) {
  // A successful auto-recovery is not an incident — emit it as a structured log
  // (enableLogs is on in sentry.ts) so we keep the "how often do stale chunks
  // happen post-deploy" signal without spawning a Sentry Issue + alert + assignee.
  Sentry.logger.info("stale chunk auto-reloaded");
}
installChunkReloadHandler();
// Browser translation rewrites React's text nodes; without this the next
// re-render throws NotFoundError and blanks the app (WEXARE-MVP-1W).
// Must run before createRoot. Logs once per page so we can see how often
// it saves a session, and whether the page was machine-translated.
let domGuardLogged = false;
installTranslateDomGuard((kind) => {
  if (domGuardLogged || !isSentryEnabled) return;
  domGuardLogged = true;
  Sentry.logger.info("translate dom guard recovered", {
    kind,
    // Google/Chrome translate tags <html> with translated-ltr / translated-rtl.
    translated: /\btranslated-(ltr|rtl)\b/.test(document.documentElement.className),
    lang: document.documentElement.lang,
  });
});
// A volunteer poster/QR link (utm_content=volunteers) must land in the
// volunteer onboarding whatever page it points at. Read before routing.
captureVolunteerCampaign(window.location.search);
// A creator poster link (wexare.com/?utm_content=creators) opens the
// organizations landing and its org onboarding. Rewrites the URL pre-router.
captureCreatorCampaign(window.location, window.history);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});
registerQueryClient(queryClient);

function AuthProvider({ children }: { children: ReactNode }) {
  const theme = useUIStore((s) => s.theme);
  // AuthProvider sits inside LanguageProvider, so useTranslation re-renders
  // this subtree on a language switch and Privy picks up the new copy.
  // Only these two strings are translatable — see PrivyLoginCopy.
  const { t } = useTranslation();

  if (useMockAuth) return <>{children}</>;

  return (
    <PrivyProvider
      appId={privyAppId!}
      config={getPrivyConfig(theme, {
        landingHeader: t("auth.loginTitle"),
        // The age line is the only eligibility text Privy lets us translate.
        loginMessage: `${t("auth.loginSubtitle")}. ${t("auth.ageRequirement")}`,
      })}
    >
      {children}
    </PrivyProvider>
  );
}

/**
 * Provides the service registry to the entire app.
 * In mock mode, uses all mock adapters.
 * In Privy mode, uses Supabase adapters (with mock fallbacks for unimplemented services).
 */
function ServiceBridge({ children }: { children: ReactNode }) {
  return useMockAuth ? (
    <MockServiceBridge>{children}</MockServiceBridge>
  ) : (
    <PrivyServiceBridge>{children}</PrivyServiceBridge>
  );
}

function MockServiceBridge({ children }: { children: ReactNode }) {
  const registry = useMemo(createMockRegistry, []);
  return <ServiceProvider registry={registry}>{children}</ServiceProvider>;
}

function PrivyServiceBridge({ children }: { children: ReactNode }) {
  const polygonClient = usePolygonClient();
  const registry = useMemo(
    () => createSupabaseRegistry(polygonClient),
    [polygonClient],
  );

  return <ServiceProvider registry={registry}>{children}</ServiceProvider>;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <HelmetProvider>
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <QueryClientProvider client={queryClient}>
              <BrowserRouter>
                <ServiceBridge>
                  <App />
                  <GlobalTransactionModal />
                  <CelebrationLayer />
                  <StaleVersionBanner />
                  <AttentionBubble />
                  <DebugPanel />
                </ServiceBridge>
              </BrowserRouter>
            </QueryClientProvider>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  </StrictMode>,
);
